import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Stack, Button, Group, SimpleGrid, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, TableShell, DataTable, KpiFilterRow, type KpiFilterItem } from '@repletoryreach/sdk-ui/data-display';
import { StatCard } from '../../../components/StatCard';
import { TableFooter } from '../../../components/TableFooter';
import { BillingToolbar } from '../components/BillingToolbar';
import { IconLedger, IconAdjustment, IconChart, IconTag } from '../components/icons';
import { Money, OrgCell } from '../components/cells';
import { useTenantMap } from '../../../data/useTenants';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatInteger, formatDateTime, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';

type Row = admin.AdminWalletAlert;

const STAT_COLS = { base: 1, sm: 2, lg: 4 };

/**
 * Every wallet the alert worker has an opinion about: empty, low, on auto
 * top-up, or recently alerted. Healthy wallets that have never crossed a line
 * are not listed; the tenant page shows any one wallet.
 */
export default function WalletAlertsPage() {
  const t = useT();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['admin', 'wallet-alerts'], queryFn: () => admin.listWalletAlerts(), refetchInterval: 30_000 });
  const { map: tenants } = useTenantMap();
  const rows = useMemo(() => data?.data ?? [], [data]);

  const [pageSize, setPageSize] = usePersistedState<number>('admin.wallet-alerts.pageSize', 25);
  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<string | null>('all');
  const [page, setPage] = useState(1);

  const counts = useMemo(() => ({
    empty: rows.filter((r) => r.state === 'empty').length,
    low: rows.filter((r) => r.state === 'low').length,
    auto: rows.filter((r) => r.auto_topup_enabled).length,
    stale: rows.filter((r) => r.state !== r.recorded_state).length
  }), [rows]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('billing.walletAlerts.kpi.all'), value: rows.length, tone: 'primary' },
    { id: 'empty', label: t('billing.walletAlerts.state.empty'), value: counts.empty, tone: 'down' },
    { id: 'low', label: t('billing.walletAlerts.state.low'), value: counts.low, tone: 'warning' },
    { id: 'auto', label: t('billing.walletAlerts.kpi.auto'), value: counts.auto, tone: 'healthy' }
  ];

  const filtered = useMemo(() => {
    let out = rows;
    if (kpi === 'empty' || kpi === 'low') out = out.filter((r) => r.state === kpi);
    if (kpi === 'auto') out = out.filter((r) => r.auto_topup_enabled);
    const q = query.trim().toLowerCase();
    if (q) out = out.filter((r) => (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q) || r.organisation_id.toLowerCase().includes(q) || (r.notify_email ?? '').toLowerCase().includes(q));
    return out;
  }, [rows, kpi, query, tenants]);

  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('billing.skuRates.eyebrow')} title={t('billing.walletAlerts.title')} description={t('billing.walletAlerts.description')} />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard withRule tone="error" icon={<IconLedger />} label={t('billing.walletAlerts.stat.empty')} value={formatInteger(counts.empty)} hint={t('billing.walletAlerts.stat.emptyHint')} loading={isLoading} />
        <StatCard withRule tone="warning" icon={<IconAdjustment />} label={t('billing.walletAlerts.stat.low')} value={formatInteger(counts.low)} hint={t('billing.walletAlerts.stat.lowHint')} loading={isLoading} />
        <StatCard withRule tone="success" icon={<IconTag />} label={t('billing.walletAlerts.stat.auto')} value={formatInteger(counts.auto)} hint={t('billing.walletAlerts.stat.autoHint')} loading={isLoading} />
        <StatCard withRule tone="info" icon={<IconChart />} label={t('billing.walletAlerts.stat.wallets')} value={formatInteger(data?.meta.wallets ?? 0)} hint={t('billing.walletAlerts.stat.walletsHint', { listed: formatInteger(rows.length) })} loading={isLoading} />
      </SimpleGrid>

      {counts.stale > 0 && <Alert color="info">{t('billing.walletAlerts.staleHint', { count: formatInteger(counts.stale) })}</Alert>}

      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi(next ?? 'all'); setPage(1); }} />

      <BillingToolbar search={query} onSearch={(v) => { setQuery(v); setPage(1); }} searchPlaceholder={t('billing.walletAlerts.search')} />

      <TableShell
        totalLabel={t('billing.walletAlerts.showingCount', { shown: formatInteger(filtered.length), total: formatInteger(rows.length) })}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={<EmptyState title={t('billing.walletAlerts.empty')} description={t('billing.walletAlerts.emptyHint')} />}
        error={error ? (
          <Alert color="error">
            <Stack gap="sm">
              <Text role="bodySmall">{t('billing.walletAlerts.loadError')}</Text>
              <Group><Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button></Group>
            </Stack>
          </Alert>
        ) : null}
        pagination={<TableFooter page={page} pageSize={pageSize} total={filtered.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} />}
      >
        <DataTable<Indexed<Row>>
          records={paged as Indexed<Row>[]}
          loading={isLoading}
          idAccessor="organisation_id"
          emptyState={t('billing.walletAlerts.empty')}
          columns={[
            { accessor: 'organisation_id', title: t('billing.usage.columns.organisation'), render: (r) => <OrgCell id={r.organisation_id} /> },
            {
              accessor: 'state', title: t('common.status'),
              render: (r) => (
                <Badge size="sm" variant="light" color={r.state === 'empty' ? 'red' : r.state === 'low' ? 'orange' : 'green'}>
                  {t(`billing.walletAlerts.state.${r.state}`, r.state)}
                </Badge>
              )
            },
            { accessor: 'balance_minor', title: t('billing.walletAlerts.columns.balance'), textAlign: 'right', render: (r) => <Money minor={r.balance_minor} currency={r.currency} signed /> },
            { accessor: 'low_balance_minor', title: t('billing.walletAlerts.columns.line'), textAlign: 'right', render: (r) => <Money minor={r.low_balance_minor} currency={r.currency} /> },
            {
              accessor: 'auto_topup_enabled', title: t('billing.walletAlerts.columns.auto'),
              render: (r) => r.auto_topup_enabled
                ? <Text role="bodySmall">{r.auto_topup_amount_minor != null ? <Money minor={r.auto_topup_amount_minor} currency={r.currency} /> : t('common.yes')}{r.payment_method ? ` · ${r.payment_method}` : ''}</Text>
                : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.no')}</Text>
            },
            {
              accessor: 'last_notification', title: t('billing.walletAlerts.columns.lastAlert'),
              render: (r) => r.last_notification
                ? <Text role="caption">{t(`billing.walletAlerts.kind.${r.last_notification.kind}`, r.last_notification.kind)} · {t(`billing.walletAlerts.channel.${r.last_notification.channel}`, r.last_notification.channel)} · <span title={formatDateTime(r.last_notification.created_at)}>{formatRelative(r.last_notification.created_at)}</span></Text>
                : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
            },
            { accessor: 'notify_email', title: t('billing.walletAlerts.columns.notify'), render: (r) => <Text role="caption" c="var(--rr-color-text-muted)">{r.notify_email ?? t('billing.walletAlerts.notifyDefault')}</Text> },
            { accessor: 'actions', title: t('common.actions'), textAlign: 'right', render: (r) => <Link to={`/tenants/${r.organisation_id}?tab=billing`}>{t('billing.walletAlerts.openTenant')}</Link> }
          ]}
        />
      </TableShell>
    </Stack>
  );
}
